import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListCountriesQuery } from '../../../../../../../../../graphql/fragment/audiences/ListCountries.generated';
import type { ListRegionsQuery } from '../../../../../../../../../graphql/fragment/audiences/ListRegions.generated';
import type { FormSegment } from '../../../../../../../../../modules/audiences/AudienceForm/types';
import { SegmentCategory } from '../../../../hooks/types';
import { useReferenceData } from '../../../../hooks/useReferenceData';
import { SegmentAlert } from '../../../expandableSections/segments/SegmentAlert';
import { createBasicInfoPropsFromSegment } from '../../../utils/createBasicInfoProps';
import { getSegmentExpirationAlertProps } from '../../../utils/getSegmentExpirationAlertProps';
import { BasicInfo } from '../SegmentListItem/BasicInfo';
import { MarketMetadata } from '../SegmentListItem/MarketMetadata';
import { SegmentHeader } from '../SegmentListItem/SegmentHeader';
import { itemStyles } from '../SegmentListItem/styles';
import { useSegmentHeaderElements } from '../SegmentListItem/useSegmentHeaderElements';
import { createSegmentMetadata } from '../SegmentsList/utils/createSegmentMetadata';
import { formatReachValue } from '../SegmentsList/utils/formatReachValue';

export type SelectedSegmentItemProps = {
  segment: FormSegment;
  onRemove: () => void;
  dataTest?: string;
  regions: ListRegionsQuery['region'];
  countries: ListCountriesQuery['country'];
};

export const SelectedSegmentItem = ({
  segment,
  onRemove,
  dataTest = 'selected-segment-item',
  regions,
  countries,
}: SelectedSegmentItemProps) => {
  const { organizations, providers } = useReferenceData();
  const { t } = useTranslation();

  const metadata = useMemo(() => createSegmentMetadata(segment, t, segment.account_id || ''), [segment, t]);
  const displayReach = formatReachValue(t, metadata.reach);
  const organizationName = segment.account_id ? organizations[segment.account_id] : null;

  const displayedOrganizationName =
    organizationName === 'OpenAudience' || segment.category === 'predefined'
      ? 'OpenXSelect'
      : organizationName || t('N / A');
  const providerName = segment.provider_id ? providers[segment.provider_id] : null;
  const displayProviderName =
    providerName === 'OpenAudience' || segment.category === 'predefined' ? 'OpenXSelect' : providerName || t('N / A');

  const headerConfig = useSegmentHeaderElements({ mode: 'removable', onRemove });
  const hasMarketMetadata = metadata.regionCodes.length > 0 || metadata.countryCodes.length > 0;

  const basicInfoProps = useMemo(
    () =>
      createBasicInfoPropsFromSegment({
        segment,
        id: metadata.id,
        reach: metadata.reach,
        displayReach,
        category: metadata.category || '',
        organizationName: displayedOrganizationName,
        providerName: displayProviderName || null,
      }),
    [segment, metadata, displayReach, displayedOrganizationName, displayProviderName]
  );

  return (
    <ListItem data-test={dataTest} sx={itemStyles({ isSelected: false })}>
      <Stack
        className="segment-list-item"
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignContent: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ minWidth: 0, overflow: 'hidden', flexGrow: 1 }}>
          <SegmentHeader
            name={metadata.name}
            showPredefinedSegmentBadge={segment.category === SegmentCategory.PREDEFINED}
            isLAL={false}
            config={headerConfig}
            isSelectedItem
            expirationAlert={<SegmentAlert {...getSegmentExpirationAlertProps(segment)} />}
          />

          <BasicInfo {...basicInfoProps} />

          {hasMarketMetadata && (
            <MarketMetadata
              regionCodes={metadata.regionCodes}
              countryCodes={metadata.countryCodes}
              regions={regions}
              countries={countries}
              dataTest="segment-market-metadata"
            />
          )}
        </Box>

        {headerConfig.rightIcon}
      </Stack>
    </ListItem>
  );
};
